import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";

const avg = a => a?.length ? +(a.reduce((x,y)=>x+y,0)/a.length).toFixed(1) : 0;

export default function AdminTeacherView() {
  const { id } = useParams();
  const nav    = useNavigate();
  const [teacher,       setTeacher]       = useState(null);
  const [contacts,      setContacts]      = useState([]);
  const [reviews,       setReviews]       = useState([]);
  const [referredUsers, setReferredUsers] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [err,           setErr]           = useState("");

  useEffect(() => {
    api.get("/admin/teachers/" + id)
      .then(r => {
        setTeacher(r.data.teacher);
        setContacts(r.data.contacts);
        setReviews(r.data.reviews);
        setReferredUsers(r.data.referredUsers || []);
      })
      .catch(() => setErr("Failed to load teacher."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={{ minHeight:"100vh", background:"#0a0a0a", display:"flex", alignItems:"center", justifyContent:"center", color:"#475569" }}>
      Loading…
    </div>
  );

  if (err || !teacher) return (
    <div style={{ minHeight:"100vh", background:"#0a0a0a", display:"flex", alignItems:"center", justifyContent:"center", color:"#ef4444" }}>
      {err || "Teacher not found."}
    </div>
  );

  const phone  = teacher.phone?.replace(/\D/g, "");
  const rating = avg(teacher.ratings);

  return (
    <div style={{ minHeight:"100vh", background:"#0a0a0a", color:"#f1f5f9", padding:"16px 12px", fontFamily:"system-ui,sans-serif" }}>
      <style>{`
        .atv-stats4  { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:16px; }
        .atv-grid2   { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
        @media(max-width:520px) {
          .atv-stats4 { grid-template-columns:repeat(2,1fr); }
          .atv-grid2  { grid-template-columns:1fr; }
        }
      `}</style>
      <div style={{ maxWidth:720, margin:"0 auto" }}>

        {/* Back */}
        <button onClick={() => nav("/admin")}
          style={{ background:"none", border:"1px solid #1e293b", color:"#94a3b8", padding:"6px 14px", borderRadius:8, fontSize:13, cursor:"pointer", marginBottom:20 }}>
          ← Back to Admin
        </button>

        {/* Header card */}
        <div style={{ background:"#111", border:"1px solid #1e293b", borderRadius:14, padding:"20px", marginBottom:16 }}>
          <div style={{ display:"flex", gap:16, alignItems:"flex-start", flexWrap:"wrap" }}>

            {/* Avatar */}
            {teacher.photo_url ? (
              <img src={teacher.photo_url} alt={teacher.name}
                style={{ width:64, height:64, borderRadius:12, objectFit:"cover", flexShrink:0 }} />
            ) : (
              <div style={{ width:64, height:64, borderRadius:12, background:"linear-gradient(135deg,#f97316,#ea580c)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:22, flexShrink:0 }}>
                {(teacher.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
              </div>
            )}

            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:20, fontWeight:800, color:"#f1f5f9" }}>{teacher.name}</div>
              <div style={{ fontSize:13, color:"#475569", marginTop:2 }}>{teacher.email}</div>
              <div style={{ fontSize:13, color:"#334155", marginTop:1 }}>{teacher.area}{teacher.city ? ", "+teacher.city : ""}</div>

              {/* Badges */}
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:8 }}>
                {teacher.listing_active && <span style={{ background:"#22c55e22", color:"#22c55e", fontSize:11, padding:"3px 10px", borderRadius:20, border:"1px solid #22c55e44", fontWeight:600 }}>Listed</span>}
                {teacher.verified       && <span style={{ background:"#0891b222", color:"#0891b2", fontSize:11, padding:"3px 10px", borderRadius:20, border:"1px solid #0891b244", fontWeight:600 }}>Verified</span>}
                {teacher.blocked        && <span style={{ background:"#ef444422", color:"#ef4444", fontSize:11, padding:"3px 10px", borderRadius:20, border:"1px solid #ef444444", fontWeight:600 }}>Blocked</span>}
                {teacher.available      && <span style={{ background:"#f9731622", color:"#f97316", fontSize:11, padding:"3px 10px", borderRadius:20, border:"1px solid #f9731644", fontWeight:600 }}>Available</span>}
              </div>
            </div>
          </div>

          {/* Contact buttons */}
          {phone && (
            <div style={{ display:"flex", gap:10, marginTop:16, flexWrap:"wrap" }}>
              <a href={"tel:+91"+phone}
                style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"10px 20px", background:"#0891b2", color:"#fff", borderRadius:8, fontWeight:700, fontSize:14, textDecoration:"none" }}>
                📞 Call — {teacher.phone}
              </a>
              <a href={"https://wa.me/91"+phone} target="_blank" rel="noopener noreferrer"
                style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"10px 20px", background:"#22c55e", color:"#fff", borderRadius:8, fontWeight:700, fontSize:14, textDecoration:"none" }}>
                WhatsApp
              </a>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="atv-stats4">
          {[
            ["Credits",     teacher.credits||0,            "#f97316"],
            ["Views",       teacher.profile_views||0,      "#0891b2"],
            ["Connections", teacher.total_connections||0,  "#22c55e"],
            ["Rating",      rating ? rating+"★" : "—",    "#f59e0b"],
          ].map(([label, value, color]) => (
            <div key={label} style={{ background:"#111", border:"1px solid #1e293b", borderRadius:10, padding:"14px 10px", textAlign:"center" }}>
              <div style={{ fontSize:20, fontWeight:800, color }}>{value}</div>
              <div style={{ fontSize:11, color:"#475569", marginTop:3 }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Referral Info */}
        <div style={{ background:"#111", border:"1px solid #f9731633", borderRadius:14, padding:"16px 18px", marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#f97316", letterSpacing:1.2, textTransform:"uppercase", marginBottom:12 }}>🔗 Referral Info</div>
          <div className="atv-grid2" style={{ marginBottom:12 }}>
            <div style={{ background:"#0a0a0a", borderRadius:8, padding:"10px 12px", border:"1px solid #1e293b" }}>
              <div style={{ fontSize:10, color:"#475569", textTransform:"uppercase", letterSpacing:1, marginBottom:3 }}>Ref Code</div>
              <div style={{ fontSize:13, fontWeight:700, color:"#f97316", fontFamily:"monospace" }}>{teacher.user?.ref_code || "—"}</div>
            </div>
            <div style={{ background:"#0a0a0a", borderRadius:8, padding:"10px 12px", border:"1px solid #1e293b" }}>
              <div style={{ fontSize:10, color:"#475569", textTransform:"uppercase", letterSpacing:1, marginBottom:3 }}>Referrals Made</div>
              <div style={{ fontSize:13, fontWeight:700, color:"#22c55e" }}>{teacher.user?.referral_count || 0}</div>
            </div>
          </div>
          {teacher.user?.referred_by_name ? (
            <div style={{ background:"#22c55e12", border:"1px solid #22c55e33", borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:12, color:"#22c55e" }}>
              ✓ Referred by: <b>{teacher.user.referred_by_name}</b>
            </div>
          ) : (
            <div style={{ fontSize:12, color:"#334155", marginBottom:10 }}>Joined directly (no referral)</div>
          )}
          {referredUsers.length > 0 && (
            <div>
              <div style={{ fontSize:11, color:"#475569", fontWeight:600, marginBottom:8 }}>Teachers/Students they referred ({referredUsers.length}):</div>
              {referredUsers.map(u => (
                <div key={u._id} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid #1e293b", fontSize:12 }}>
                  <span style={{ color:"#f1f5f9", fontWeight:600 }}>{u.name}</span>
                  <span style={{ color:"#475569" }}>{u.email} · {u.role} · {new Date(u.createdAt).toLocaleDateString("en-IN")}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bio + Details */}
        <div style={{ background:"#111", border:"1px solid #1e293b", borderRadius:14, padding:"18px", marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#475569", letterSpacing:1.2, textTransform:"uppercase", marginBottom:12 }}>Profile Details</div>

          {teacher.bio && (
            <div style={{ fontSize:13, color:"#94a3b8", lineHeight:1.6, marginBottom:14, background:"#0a0a0a", padding:"10px 14px", borderRadius:8, border:"1px solid #1e293b" }}>
              {teacher.bio}
            </div>
          )}

          <div className="atv-grid2">
            {[
              ["Experience",    teacher.experience ? teacher.experience+" yrs" : "—"],
              ["Fee/mo",        teacher.fees_per_hour ? `₹${teacher.fees_per_hour}/mo` : "—"],
              ["Schedule",      teacher.schedule_type === "regular" ? "Regular (6d/wk)" : teacher.schedule_type === "alternate" ? "Alternate (3d/wk)" : "—"],
              ["Demo Class",    teacher.offers_demo_class === false ? "No" : "Yes"],
              ["Plan",          teacher.is_paid_plan ? "Paid Plan" : "Free Plan"],
              ["Demo Pending",  teacher.demo_awaiting_payment ? "⏳ Yes — Awaiting Fee" : "No"],
              ["Lead Price",    teacher.lead_unlock_price != null ? `₹${teacher.lead_unlock_price} (custom)` : "Default"],
              ["Joined",        teacher.createdAt ? new Date(teacher.createdAt).toLocaleDateString("en-IN") : "—"],
            ].map(([label, value]) => (
              <div key={label} style={{ background:"#0a0a0a", borderRadius:8, padding:"10px 12px", border:"1px solid #1e293b" }}>
                <div style={{ fontSize:10, color:"#475569", textTransform:"uppercase", letterSpacing:1, marginBottom:3 }}>{label}</div>
                <div style={{ fontSize:13, fontWeight:600, color:"#f1f5f9" }}>{value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Listing Payment Info */}
        {teacher.listing_cf_order_id && (
          <div style={{ background:"#111", border:"1px solid #a78bfa33", borderRadius:14, padding:"16px 18px", marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#a78bfa", letterSpacing:1.2, textTransform:"uppercase", marginBottom:10 }}>💳 Last Listing Payment</div>
            <div className="atv-grid2">
              {[
                ["Amount Paid",  teacher.listing_amount ? `₹${teacher.listing_amount}` : "—"],
                ["Paid At",      teacher.listing_paid_at ? new Date(teacher.listing_paid_at).toLocaleString("en-IN", { dateStyle:"medium", timeStyle:"short" }) : "—"],
              ].map(([label, value]) => (
                <div key={label} style={{ background:"#0a0a0a", borderRadius:8, padding:"10px 12px", border:"1px solid #1e293b" }}>
                  <div style={{ fontSize:10, color:"#475569", textTransform:"uppercase", letterSpacing:1, marginBottom:3 }}>{label}</div>
                  <div style={{ fontSize:13, fontWeight:600, color:"#f1f5f9" }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:10, fontSize:11, color:"#64748b", wordBreak:"break-all" }}>
              Cashfree Order: <span style={{ fontFamily:"monospace", color:"#94a3b8" }}>{teacher.listing_cf_order_id}</span>
            </div>
          </div>
        )}

        {/* Teaching Structure */}
        {teacher.teaching?.length > 0 && (
          <div style={{ background:"#111", border:"1px solid #1e293b", borderRadius:14, padding:"16px 18px", marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#475569", letterSpacing:1.2, textTransform:"uppercase", marginBottom:10 }}>📘 Teaching</div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {teacher.teaching.map((entry, i) => (
                <div key={i} style={{ fontSize:13, color:"#e2e8f0" }}>
                  <span style={{ color:"#f97316", fontWeight:700, marginRight:6 }}>•</span>
                  <span style={{ fontWeight:600 }}>{entry.class}</span>
                  <span style={{ color:"#64748b", marginLeft:6 }}>{entry.subjects?.join(", ") || "All"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subjects / Classes fallback */}
        {(!teacher.teaching?.length) && teacher.subjects?.length > 0 && (
          <div style={{ background:"#111", border:"1px solid #1e293b", borderRadius:14, padding:"16px 18px", marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#475569", letterSpacing:1.2, textTransform:"uppercase", marginBottom:10 }}>Subjects</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {teacher.subjects.map(s => (
                <span key={s} style={{ background:"#f9731615", color:"#f97316", fontSize:12, padding:"4px 10px", borderRadius:6, border:"1px solid #f9731630" }}>{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {teacher.education && (teacher.education.school_10_name || teacher.education.school_10_board || teacher.education.degree_name) && (
          <div style={{ background:"#111", border:"1px solid #1e293b", borderRadius:14, padding:"16px 18px", marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#475569", letterSpacing:1.2, textTransform:"uppercase", marginBottom:10 }}>🎓 Education</div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {(teacher.education.school_10_name || teacher.education.school_10_board) && (
                <div style={{ fontSize:13, color:"#e2e8f0" }}>
                  <span style={{ color:"#0891b2", fontWeight:700, marginRight:6 }}>Class 10:</span>
                  {[teacher.education.school_10_name, teacher.education.school_10_board, teacher.education.school_10_year].filter(Boolean).join(" · ")}
                </div>
              )}
              {(teacher.education.school_12_name || teacher.education.school_12_board) && (
                <div style={{ fontSize:13, color:"#e2e8f0" }}>
                  <span style={{ color:"#0891b2", fontWeight:700, marginRight:6 }}>Class 12:</span>
                  {[teacher.education.school_12_name, teacher.education.school_12_board, teacher.education.school_12_year].filter(Boolean).join(" · ")}
                </div>
              )}
              {teacher.education.degree_name && (
                <div style={{ fontSize:13, color:"#e2e8f0" }}>
                  <span style={{ color:"#0891b2", fontWeight:700, marginRight:6 }}>Degree:</span>
                  {[teacher.education.degree_name, teacher.education.college_name, teacher.education.degree_status].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Students who unlocked */}
        <div style={{ background:"#111", border:"1px solid #1e293b", borderRadius:14, padding:"16px 18px", marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#475569", letterSpacing:1.2, textTransform:"uppercase", marginBottom:12 }}>
            Students Unlocked ({contacts.length})
          </div>
          {contacts.length === 0 ? (
            <div style={{ fontSize:13, color:"#334155" }}>No students yet.</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {contacts.map(c => (
                <div key={c._id} style={{ background:"#0a0a0a", borderRadius:8, padding:"10px 14px", border:"1px solid #1e293b", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:"#f1f5f9" }}>{c.student?.name || "Unknown"}</div>
                    <div style={{ fontSize:11, color:"#475569" }}>{c.student?.email}</div>
                    <div style={{ fontSize:11, color:"#334155" }}>{c.createdAt ? new Date(c.createdAt).toLocaleDateString("en-IN") : ""}</div>
                  </div>
                  {c.student?.phone && (
                    <a href={"tel:+91"+c.student.phone.replace(/\D/g,"")}
                      style={{ fontSize:11, padding:"4px 12px", background:"#0891b222", color:"#0891b2", border:"1px solid #0891b244", borderRadius:6, textDecoration:"none", fontWeight:600 }}>
                      📞 {c.student.phone}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reviews */}
        {reviews.length > 0 && (
          <div style={{ background:"#111", border:"1px solid #1e293b", borderRadius:14, padding:"16px 18px", marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#475569", letterSpacing:1.2, textTransform:"uppercase", marginBottom:12 }}>
              Reviews ({reviews.length})
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {reviews.map(r => (
                <div key={r._id} style={{ background:"#0a0a0a", borderRadius:8, padding:"10px 14px", border:"1px solid #1e293b" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:"#f1f5f9" }}>{r.studentName || "Student"}</span>
                    <span style={{ fontSize:13, color:"#f59e0b" }}>{"★".repeat(r.rating||0)}</span>
                  </div>
                  {r.comment && <div style={{ fontSize:12, color:"#94a3b8" }}>{r.comment}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
